import { useState, useCallback, useRef, useEffect, useMemo } from 'react'

interface DoujinsMetadata {
  pageCount: number
  tags: string[]
  hasAudio: boolean
  /** The platform screen refused this gallery (its tags or title are blocked for this viewer). */
  blocked?: boolean
  /** The lookup failed twice; the card is shown anyway (opening the album checks its tags again). */
  failed?: boolean
}

const metadataCache = new Map<string, DoujinsMetadata>()
const pendingRequests = new Map<string, Promise<DoujinsMetadata>>()

// Throttle: Only allow a few concurrent requests at a time
let activeRequests = 0
const MAX_CONCURRENT_REQUESTS = 4
const requestQueue: Array<() => void> = []

function processQueue() {
  if (activeRequests >= MAX_CONCURRENT_REQUESTS || requestQueue.length === 0) {
    return
  }

  const nextRequest = requestQueue.shift()
  if (nextRequest) {
    nextRequest()
  }
}

/** Sources whose grid cards carry no real tags: each gallery's own page is read (by these routes) for its tags and page count. */
const METADATA_ROUTE: Record<string, string> = {
  doujins: '/api/booru/doujins/metadata',
  hentaiera: '/api/booru/hentaiera/metadata',
  goontai: '/api/booru/goontai/metadata',
  rule34lol: '/api/booru/rule34lol/metadata',
}

async function requestMetadata(site: string, galleryUrl: string): Promise<DoujinsMetadata> {
  const response = await fetch(`${METADATA_ROUTE[site]}?url=${encodeURIComponent(galleryUrl)}`)
  // 404 is the platform screen's "not available": this gallery is blocked for this viewer.
  if (response.status === 404) return { pageCount: 0, tags: [], hasAudio: false, blocked: true }
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const data = await response.json()
  return { pageCount: data.pageCount || 0, tags: data.tags || [], hasAudio: data.hasAudio || false }
}

/**
 * One shared, throttled lookup of a gallery's metadata (cached for the session, one request per gallery however many callers ask).
 * A failed lookup is tried once more, then settles as `failed` so a flaky source cannot hold the grid back forever.
 */
export function loadGalleryMetadata(site: string, galleryUrl: string): Promise<DoujinsMetadata> {
  const cached = metadataCache.get(galleryUrl)
  if (cached) return Promise.resolve(cached)
  const pending = pendingRequests.get(galleryUrl)
  if (pending) return pending

  const promise = new Promise<DoujinsMetadata>((resolve) => {
    const makeRequest = async () => {
      activeRequests++
      try {
        let meta: DoujinsMetadata
        try {
          meta = await requestMetadata(site, galleryUrl)
        } catch {
          await new Promise((r) => setTimeout(r, 1500))
          meta = await requestMetadata(site, galleryUrl)
        }
        metadataCache.set(galleryUrl, meta)
        resolve(meta)
      } catch (err) {
        console.error('[useDoujinsMetadata] Metadata lookup failed:', galleryUrl, err)
        const failed: DoujinsMetadata = { pageCount: 0, tags: [], hasAudio: false, failed: true }
        metadataCache.set(galleryUrl, failed)
        resolve(failed)
      } finally {
        pendingRequests.delete(galleryUrl)
        activeRequests--
        processQueue()
      }
    }

    // Add to queue if at capacity, otherwise execute immediately
    if (activeRequests >= MAX_CONCURRENT_REQUESTS) requestQueue.push(makeRequest)
    else makeRequest()
  })

  pendingRequests.set(galleryUrl, promise)
  return promise
}

interface ScreenablePost {
  id: string | number
  site?: string | null
  type?: string | null
  rawPost?: { galleryUrl?: string; isCategory?: boolean; slug?: string } | null
  tags?: readonly string[] | string | null
}

/** A grid card for a real gallery on a source whose listing has no tags (not a category tile, not one page of an opened album). */
function galleryToScreen(post: ScreenablePost): { site: string; galleryUrl: string } | null {
  const site = post.site ?? ''
  // Goontai's cards are videos, found by their slug (the page address its metadata route reads).
  if (site === 'goontai') return post.rawPost?.slug ? { site, galleryUrl: `https://hentaiocean.com/watch/${post.rawPost.slug}` } : null
  // Rule34.lol's cards are posts, found by their number (the page address its metadata route reads).
  if (site === 'rule34lol') return /^\d+$/.test(String(post.id)) ? { site, galleryUrl: `https://rule34.lol/images/${post.id}` } : null
  const galleryUrl = post.rawPost?.galleryUrl
  if (!METADATA_ROUTE[site] || post.type !== 'album' || !galleryUrl) return null
  if (post.rawPost?.isCategory || galleryUrl.startsWith('category:')) return null
  return { site, galleryUrl }
}

/**
 * Platform tags for sources whose listings carry no tags (Doujins, HentaiEra, Goontai): each gallery's tags are looked up (saved, so each is read
 * once) and a card is shown only after it passed; a blocked one never appears. Posts of other sources pass straight through.
 */
export function useGalleryScreen<T extends ScreenablePost>(posts: readonly T[]): T[] {
  const [, setChecked] = useState(0)

  useEffect(() => {
    let alive = true
    for (const post of posts) {
      const target = galleryToScreen(post)
      if (target && !metadataCache.has(target.galleryUrl)) {
        loadGalleryMetadata(target.site, target.galleryUrl).then(() => {
          if (alive) setChecked((n) => n + 1)
        })
      }
    }
    return () => {
      alive = false
    }
  }, [posts])

  // Recomputed whenever the cache gets a new entry (its size is part of the key): results land in the module cache, and the counter above
  // triggers the render.
  return useMemo(
    () => screenedPosts(posts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [posts, metadataCache.size],
  )
}

/** Goontai's posts with their genres as tags, made once per post so a card keeps the same object while other lookups arrive. */
const withGenres = new WeakMap<object, object>()

/**
 * The posts that may be shown now: everything that needs no lookup, plus galleries whose lookup finished and was not blocked. A Goontai video
 * (its feed carries no tags) comes back with the genres that were looked up as its tags, so the viewer and the tag lists can show them.
 */
export function screenedPosts<T extends ScreenablePost>(posts: readonly T[]): T[] {
  const shown: T[] = []
  for (const post of posts) {
    const target = galleryToScreen(post)
    if (!target) {
      shown.push(post)
      continue
    }
    const verdict = metadataCache.get(target.galleryUrl)
    if (!verdict || verdict.blocked) continue
    const own = Array.isArray(post.tags) ? post.tags : typeof post.tags === 'string' && post.tags ? post.tags.split(/\s+/) : []
    // Goontai's cards carry no tags at all; Rule34.lol's carry only copyright, character and artist, so the looked-up tags are added to those.
    const completes = (target.site === 'goontai' && own.length === 0) || target.site === 'rule34lol'
    if (completes && verdict.tags.length > 0) {
      let enriched = withGenres.get(post)
      if (!enriched) {
        enriched = { ...post, tags: Array.from(new Set([...own, ...verdict.tags])) }
        withGenres.set(post, enriched)
      }
      shown.push(enriched as T)
    } else {
      shown.push(post)
    }
  }
  return shown
}

/** For tests: forget every saved answer. */
export function clearGalleryMetadata(): void {
  metadataCache.clear()
  pendingRequests.clear()
}

export function useDoujinsMetadata() {
  const [metadata, setMetadata] = useState<Map<string, DoujinsMetadata>>(new Map())
  const observerRef = useRef<IntersectionObserver | null>(null)
  const observedElements = useRef<Map<string, HTMLElement>>(new Map())
  const observedPosts = useRef<Set<string>>(new Set()) // Track which posts we've already set up observers for

  // Set up intersection observer to auto-fetch when visible
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const postId = entry.target.getAttribute('data-post-id')
            const galleryUrl = entry.target.getAttribute('data-gallery-url')

            if (postId && galleryUrl) {
              fetchMetadata(galleryUrl, postId)
            }
          }
        })
      },
      {
        rootMargin: '200px', // Start loading before visible
        threshold: 0.1
      }
    )

    return () => {
      observerRef.current?.disconnect()
    }
  }, [])

  const fetchMetadata = useCallback(async (galleryUrl: string, postId: string) => {
    // The ticker only asks for Doujins cards (HentaiEra cards are looked up by useGalleryScreen); the shared loader saves each answer.
    const meta = await loadGalleryMetadata('doujins', galleryUrl)
    setMetadata(prev => new Map(prev).set(postId, meta))
  }, [])

  const observePost = useCallback((element: HTMLElement, postId: string, galleryUrl: string) => {
    if (!observerRef.current) {
      return
    }

    // Skip if we've already observed this post
    if (observedPosts.current.has(postId)) {
      return
    }

    element.setAttribute('data-post-id', postId)
    element.setAttribute('data-gallery-url', galleryUrl)
    observerRef.current.observe(element)
    observedElements.current.set(postId, element)
    observedPosts.current.add(postId)

    // Also immediately fetch if element is already visible
    const rect = element.getBoundingClientRect()
    const isVisible = rect.top < window.innerHeight + 200 && rect.bottom > -200
    if (isVisible) {
      fetchMetadata(galleryUrl, postId)
    }
  }, [fetchMetadata])

  const unobservePost = useCallback((postId: string) => {
    const element = observedElements.current.get(postId)
    if (element && observerRef.current) {
      observerRef.current.unobserve(element)
      observedElements.current.delete(postId)
      observedPosts.current.delete(postId)
    }
  }, [])

  const clearObservedPosts = useCallback(() => {
    observedPosts.current.clear()
  }, [])

  const getMetadata = useCallback((postId: string): DoujinsMetadata | null => {
    return metadata.get(postId) || null
  }, [metadata])

  return {
    observePost,
    unobservePost,
    getMetadata,
    clearObservedPosts
  }
}
